import { useCart } from "@/contexts/CartContext";
import { formatPrice } from "@/utils/format";
import type { DiscountValidation } from "@/types/order";

interface OrderSummaryProps {
  appliedDiscount: DiscountValidation | null;
}

export function OrderSummary({ appliedDiscount }: OrderSummaryProps) {
  const { items, subtotal } = useCart();

  const discountAmount = appliedDiscount?.valid
    ? Math.round(subtotal * ((appliedDiscount.discountPercent || 0) / 100))
    : 0;
  const total = subtotal - discountAmount;

  return (
    <div className="bg-card border border-border rounded-xl p-6 sticky top-24">
      <h2 className="text-lg font-semibold mb-4">Resumen del Pedido</h2>
      
      <div className="space-y-4 mb-6">
        {items.map((item) => (
          <div key={item.id} className="flex gap-3">
            <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
              <img
                src={item.imageUrl || "/placeholder.svg"}
                alt={item.name}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
              <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
                {item.quantity}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm line-clamp-2">{item.name}</p>
              {item.variationName && (
                <p className="text-xs text-muted-foreground">{item.variationName}</p>
              )}
              <p className="text-sm font-medium mt-1">
                {formatPrice(item.price * item.quantity)}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-border pt-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Subtotal</span>
          <span>{formatPrice(subtotal)}</span>
        </div>
        
        {discountAmount > 0 && (
          <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
            <span>Descuento ({appliedDiscount?.discountPercent}%)</span>
            <span>-{formatPrice(discountAmount)}</span>
          </div>
        )}
        
        <div className="flex justify-between text-lg font-bold pt-2 border-t border-border">
          <span>Total</span>
          <span className="text-primary">{formatPrice(total)}</span>
        </div>
      </div>

      <p className="text-xs text-muted-foreground mt-4 text-center">
        Nos contactaremos para coordinar el pago y la entrega
      </p>
    </div>
  );
}
